const Field = {
  parts: [
    'inputGroup',
    'input',
    'label',
    'passwordIconButtonContainer',
    'passwordIconButton',
    'passwordIcon',
    'checkbox',
    'select',
    'textarea',
    'fileInput',
    'fileInputContainer',
    'fileInputIcon',
    'fileInputPrimaryText',
    'fileInputSecondaryText',
    'fileInputErrorAlert',
    'fileInputErrorAlertIcon'
  ],
  baseStyle: {
    inputGroup: {
      gap: 2,
      position: 'relative',
    },
    input: {
      height: 13.5,
      px: 4.5,
      py: 0,
      pt: 4.5, // Top padding for normal state (centers text)
      pb: 4.5, // Bottom padding for normal state (centers text)
      alignItems: 'center',
      gap: 2.5,
      alignSelf: 'stretch',
      borderRadius: 'base', // 4px
      border: '1px solid',
      borderColor: 'brandColors.greyscale10',
      backgroundColor: 'white',
      color: 'brandColors.greyscale',
      fontSize: 'sm',
      fontStyle: 'normal',
      fontWeight: 'normal',
      lineHeight: '120%',
      transition: 'all 0.2s ease-in-out',
      _focus: {
        backgroundColor: 'white',
        borderWidth: '1.5px',
        borderColor: 'brandColors.primaryDarkLinen',
        boxShadow: 'none',
        outline: 'none',
        pt: 6.875, // More top padding when focused (text moves down)
        pb: 2.375, // Less bottom padding when focused
      },
      sx: {
        // When input has value, also move text down
        '&:not(:placeholder-shown)': {
          pt: 6.875,
          pb: 2.375,
        },
      },
      _hover: {
        borderColor: 'brandColors.greyscale10',
        backgroundColor: 'white',
      },
      _invalid: {
        borderWidth: '1.5px',
        borderColor: 'brandColors.error',
        _focus: {
          borderColor: 'brandColors.error',
        },
      },
      _disabled: {
        backgroundColor: 'brandColors.disabledBG',
        borderWidth: '1.5px',
        borderColor: 'brandColors.greyscale10',
        color: 'brandColors.greyscale40',
        cursor: 'not-allowed',
        opacity: 1,
      },
      _placeholder: {
        color: 'brandColors.greyscale70',
        opacity: 1,
      },
    },
    label: {
      position: 'absolute',
      top: 4.5,
      left: 4.5,
      color: 'brandColors.greyscale80',
      fontSize: 'xs', // 12px
      fontStyle: 'normal',
      fontWeight: 'normal',
      lineHeight: '120%', // 1.2
      fontFamily: 'body',
      transition: 'all 0.2s ease-in-out',
      cursor: 'text',
      zIndex: 1,
      pointerEvents: 'none',
      opacity: 0, // Hidden in normal state
      sx: {
        'input:not(:placeholder-shown) ~ &, input:focus ~ &': {
          opacity: 1, // Show label when focused or has value
          top: 2,
          color: 'brandColors.primaryDarkLinen',
          fontSize: '12px',
          fontWeight: 'normal',
          lineHeight: '120%',
        },
        'input:invalid ~ &': {
          opacity: 1, // Show label when invalid
          color: 'brandColors.error',
        },
        'input:disabled ~ &': {
          opacity: 1, // Show label even when disabled
          color: 'brandColors.greyscale40',
        },
      },
    },
    passwordIconButtonContainer: {
      height: 13.5,
      width: 13,
    },
    passwordIconButton: {
      height: 'full',
      width: 'full',
    },
    passwordIcon: {
      width: 5, // 20px
      height: 5, // 20px
      color: 'brandColors.primaryDarkLinen',
    },
    checkbox: {},
    select: {
      height: 13.5, // 54px
      px: 4.5, // 18px
      py: 0,
      pt: 4.5, // Top padding for normal state (centers text)
      pb: 4.5, // Bottom padding for normal state (centers text)
      borderRadius: 'base', // 4px
      border: '1px solid',
      borderColor: 'brandColors.greyscale10',
      backgroundColor: 'white',
      color: 'brandColors.greyscale',
      fontSize: 'sm', // 14px
      fontStyle: 'normal',
      fontWeight: 'normal',
      lineHeight: '120%',
      transition: 'all 0.2s ease-in-out',
      _focus: {
        backgroundColor: 'white',
        borderWidth: '1.5px',
        borderColor: 'brandColors.primaryDarkLinen',
        boxShadow: 'none',
        outline: 'none',
      },
      _hover: {
        borderColor: 'brandColors.greyscale10',
        backgroundColor: 'white',
      },
      _invalid: {
        borderWidth: '1.5px',
        borderColor: 'brandColors.error',
        _focus: {
          borderColor: 'brandColors.error',
        },
      },
      _disabled: {
        backgroundColor: 'brandColors.disabledBG',
        borderWidth: '1.5px',
        borderColor: 'brandColors.greyscale10',
        color: 'brandColors.greyscale40',
        cursor: 'not-allowed',
        opacity: 1,
      },
      _placeholder: {
        color: 'brandColors.greyscale70',
        opacity: 1,
      },
    },
    textarea: {
      minH: '120px', // Minimum height for textarea
      px: 4.5, // 18px
      py: 4.5, // 18px
      borderRadius: 'base', // 4px
      border: '1px solid',
      borderColor: 'brandColors.greyscale10',
      backgroundColor: 'white',
      color: 'brandColors.greyscale',
      fontSize: 'sm', // 14px
      fontStyle: 'normal',
      fontWeight: 'normal',
      lineHeight: '120%',
      fontFamily: 'body',
      transition: 'all 0.2s ease-in-out',
      resize: 'vertical', // Allow vertical resizing
      _focus: {
        backgroundColor: 'white',
        borderWidth: '1.5px',
        borderColor: 'brandColors.primaryDarkLinen',
        boxShadow: 'none',
        outline: 'none',
      },
      _hover: {
        borderColor: 'brandColors.greyscale10',
        backgroundColor: 'white',
      },
      _invalid: {
        borderWidth: '1.5px',
        borderColor: 'brandColors.error',
        _focus: {
          borderColor: 'brandColors.error',
        },
      },
      _disabled: {
        backgroundColor: 'brandColors.disabledBG',
        borderWidth: '1.5px',
        borderColor: 'brandColors.greyscale10',
        color: 'brandColors.greyscale40',
        cursor: 'not-allowed',
        opacity: 1,
      },
      _placeholder: {
        color: 'brandColors.greyscale70',
        opacity: 1,
      },
    },
    fileInput: {
      // Hidden native file input
      position: 'absolute',
      opacity: 0,
      width: 0,
      height: 0,
      overflow: 'hidden',
    },
    fileInputContainer: {
      // Container for the custom file input UI
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 3, // 12px
      px: 31, // 124px (31 * 4 = 124px)
      py: 6, // 24px (6 * 4 = 24px)
      borderRadius: 'base', // 4px
      border: '2px dashed',
      borderColor: 'brandColors.greyscale20', // rgba(27,26,24,0.2) - always 2px to keep size consistent
      backgroundColor: 'white',
      cursor: 'pointer',
      transition: 'all 0.2s ease-in-out',
      boxSizing: 'border-box', // Ensure border is included in size calculation
      position: 'relative', // For positioning context
      _hover: {
        borderColor: 'brandColors.primaryLinen100', // #c6b5a1
        backgroundColor: 'brandColors.greyscale10', // rgba(27,26,24,0.1)
      },
    },
    fileInputIcon: {
      width: 5, // 20px
      height: 5, // 20px
      color: 'brandColors.greyscale90', // rgba(50,49,47,0.9) or #1c1c1c
      flexShrink: 0,
    },
    fileInputPrimaryText: {
      fontSize: 'md', // 16px
      fontWeight: '600', // SemiBold
      lineHeight: '140%', // 1.4
      color: 'brandColors.greyscale90', // rgba(50,49,47,0.9) or #1c1c1c
      textAlign: 'center',
      fontFamily: 'body',
    },
    fileInputSecondaryText: {
      fontSize: 'md', // 16px
      fontWeight: 'normal', // Regular
      lineHeight: '140%', // 1.4
      color: 'brandColors.greyscale90', // rgba(50,49,47,0.9) or #1c1c1c
      textAlign: 'center',
      fontFamily: 'body',
    },
    fileInputErrorAlert: {
      // Error alert below file input container
      mt: 3, // 12px gap
      px: 4, // 16px
      py: 1, // 4px
      borderRadius: 'base', // 4px
      border: '1px solid',
      borderColor: 'brandColors.error', // #9d2a1e
      backgroundColor: 'brandColors.errorBG', // #fef2f2
      display: 'flex',
      alignItems: 'center',
      gap: 2, // 8px
      fontSize: 'xs', // 12px
      fontFamily: 'body',
      fontWeight: 'normal',
      lineHeight: '120%', // 1.2
      color: 'brandColors.greyscale90', // rgba(27,26,24,0.9)
    },
    fileInputErrorAlertIcon: {
      width: 5, // 20px
      height: 5, // 20px
      color: 'brandColors.error', // #9d2a1e - match border color
      flexShrink: 0,
    },
  },
  variants: {
    success: {
      input: {
        backgroundColor: 'brandColors.secondaryBG',
        borderWidth: '1.5px',
        borderColor: 'brandColors.greyscale10',
      },
    },
    fileError: {
      fileInputContainer: {
        borderWidth: '1px',
        borderColor: 'brandColors.greyscale10', // rgba(27,26,24,0.1)
        backgroundColor: 'white',
        px: 0, // 0px horizontal padding in error state
      },
    },
  },
};

export default Field;
